import type { Named, Uuid } from "@shared/api";

/**
 * Слова-фильтры поиска, как в Discord: «from:», «in:», «has:», «mentions:» (и
 * по-русски «от:», «в:», «имеет:» или «есть:», «упоминание:») прямо в строке.
 * Законченное слово уходит из строки в фильтр, а незаконченное подсказывает:
 * людей, каналы или что бывает в сообщении.
 */
export type Has = "link" | "file" | "image";

export const HAS: Record<string, Has> = {
  link: "link",
  ссылка: "link",
  ссылку: "link",
  file: "file",
  файл: "file",
  вложение: "file",
  image: "image",
  картинка: "image",
  картинку: "image",
  изображение: "image",
};

/** Что подсказывать: людей, каналы или вид содержимого. */
export type TokenKey = "from" | "in" | "has" | "mentions";

const KEYS: Record<string, TokenKey> = {
  from: "from",
  от: "from",
  in: "in",
  в: "in",
  has: "has",
  есть: "has",
  имеет: "has",
  mentions: "mentions",
  упоминание: "mentions",
};

const PENDING = /(?:^|\s)(from|от|in|в|has|есть|имеет|mentions|упоминание):(\S*)$/iu;

/** Незаконченное слово-фильтр в конце строки: какое и что после двоеточия набрано. */
export function pendingKey(text: string): { key: TokenKey; needle: string } | null {
  const match = PENDING.exec(text);
  return match ? { key: KEYS[match[1].toLowerCase()], needle: match[2].toLowerCase() } : null;
}

/** Строка без незаконченного слова-фильтра — после выбора из подсказки. */
export function dropKey(text: string): string {
  return text.replace(/(^|\s)(?:from|от|in|в|has|есть|имеет|mentions|упоминание):\S*$/iu, "$1");
}

/** Незаконченное «from:» в конце строки — то, что набрано после двоеточия. */
export function pendingFrom(text: string): string | null {
  const match = /(?:^|\s)(?:from|от):(\S*)$/iu.exec(text);
  return match ? match[1].toLowerCase() : null;
}

/** Кого подсказать: имя, ник или видимое имя начинается с набранного. */
export function matchPeople<T extends Named>(people: readonly T[], needle: string, limit = 8): T[] {
  return people
    .filter(
      (person) =>
        !needle ||
        person.username.toLowerCase().startsWith(needle) ||
        person.handle.toLowerCase().startsWith(needle) ||
        (person.display_name ?? "").toLowerCase().startsWith(needle),
    )
    .slice(0, limit);
}

/** Строка без незаконченного «from:» — после выбора человека из подсказки. */
export function dropPending(text: string): string {
  return text.replace(/(^|\s)(?:from|от):\S*$/iu, "$1");
}

/**
 * Законченные слова-фильтры (за ними пробел) — из строки в фильтры. Неузнанное
 * остаётся в строке как есть: может, это и правда искали.
 */
/**
 * Даты — как «before:», «after:» и «during:» в Discord (и по-русски «до:»,
 * «после:», «дата:»). Пишут 2026-10-30, 30.10.2026 или 30.10 (этот год).
 */
const DATES: Record<string, "before" | "after" | "during"> = {
  before: "before",
  до: "before",
  after: "after",
  после: "after",
  during: "during",
  on: "during",
  дата: "during",
};

/** День в виде `YYYY-MM-DD` — как в поле выбора даты; `null` — не дата. */
export function dayOf(value: string, now: Date = new Date()): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  const dotted = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?$/u.exec(value);
  const [year, month, date] = iso
    ? [Number(iso[1]), Number(iso[2]), Number(iso[3])]
    : dotted
      ? [dotted[3] ? Number(dotted[3]) : now.getFullYear(), Number(dotted[2]), Number(dotted[1])]
      : [0, 0, 0];
  if (!year) return null;
  const at = new Date(year, month - 1, date);
  if (at.getFullYear() !== year || at.getMonth() !== month - 1 || at.getDate() !== date) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(date).padStart(2, "0")}`;
}

export function takeTokens(
  text: string,
  people: readonly Named[],
  channels: readonly { id: Uuid; name: string }[] = [],
): {
  text: string;
  author: Uuid | null;
  has: Has | null;
  channel: Uuid | null;
  mention: Uuid | null;
  after: string | null;
  before: string | null;
} {
  let author: Uuid | null = null;
  let has: Has | null = null;
  let channel: Uuid | null = null;
  let mention: Uuid | null = null;
  let after: string | null = null;
  let before: string | null = null;
  // Имя не уникально — различает номер в хэндле. Голое имя берётся, только
  // если такой один; иначе слово остаётся, и выбор — за подсказкой.
  const personOf = (wanted: string) => {
    const byHandle = people.find((item) => item.handle.toLowerCase() === wanted);
    const byName = people.filter((item) => item.username.toLowerCase() === wanted);
    return byHandle ?? (byName.length === 1 ? byName[0] : undefined);
  };
  const rest = text.replace(
    /(^|\s)(from|от|in|в|has|есть|имеет|mentions|упоминание|before|до|after|после|during|on|дата):(\S+)(?=\s)/giu,
    (whole, lead: string, raw: string, value: string) => {
      const wanted = value.toLowerCase().replace(/^#/u, "");
      const date = DATES[raw.toLowerCase()];
      if (date) {
        const found = dayOf(wanted);
        if (!found) return whole;
        if (date !== "before") after = found;
        if (date !== "after") before = found;
        return lead;
      }
      const key = KEYS[raw.toLowerCase()];
      if (key === "has") {
        if (!HAS[wanted]) return whole;
        has = HAS[wanted];
        return lead;
      }
      if (key === "in") {
        const found = channels.filter((item) => item.name.toLowerCase() === wanted);
        if (found.length !== 1) return whole;
        channel = found[0].id;
        return lead;
      }
      const person = personOf(wanted);
      if (!person) return whole;
      if (key === "mentions") mention = person.id;
      else author = person.id;
      return lead;
    },
  );
  // Убранное слово оставляет пробелы — схлопнуть их, чтобы строка не росла.
  return { text: rest.replace(/\s{2,}/gu, " ").replace(/^\s/u, ""), author, has, channel, mention, after, before };
}
